import { useState, useRef, useCallback, useEffect } from 'react'

interface PatientFormData {
  lastName?: string
  firstName?: string
  gender?: string
  birthDate?: string
  diseaseName?: string
  diseaseDetail?: string
  treatment?: string
}

interface PatientVoiceInputProps {
  onFormUpdate: (data: Partial<PatientFormData>) => void
  autoStart?: boolean
}

const DEEPGRAM_API_KEY = '38a52e742628c2b53d5d0d72cbb75399297fc6fd'
const GEMINI_API_KEY = 'AIzaSyDqjjjVZKZyoDCjfwLRaHeT0ypfuMzOYKs'
const AUTO_START_KEY = 'fmt-japan-patient-voice-auto-start'

export default function PatientVoiceInput({ onFormUpdate, autoStart = false }: PatientVoiceInputProps) {
  const [isRecording, setIsRecording] = useState(false)
  const [liveTranscript, setLiveTranscript] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [extractedData, setExtractedData] = useState<PatientFormData>({})
  const [autoStartEnabled, setAutoStartEnabled] = useState(() => {
    const saved = localStorage.getItem(AUTO_START_KEY)
    return saved ? JSON.parse(saved) : false
  })
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const socketRef = useRef<WebSocket | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const confirmedTextRef = useRef<string>('')
  const lastProcessedRef = useRef<string>('')
  const processingIntervalRef = useRef<number | null>(null)
  const hasAutoStarted = useRef(false)

  // 自動開始設定の保存
  const handleAutoStartChange = useCallback((enabled: boolean) => {
    setAutoStartEnabled(enabled)
    localStorage.setItem(AUTO_START_KEY, JSON.stringify(enabled))
  }, [])

  // Gemini APIを使って患者情報を抽出
  const extractPatientInfo = useCallback(async (text: string) => {
    if (!text.trim() || text === lastProcessedRef.current) return
    
    lastProcessedRef.current = text
    setIsProcessing(true)

    try {
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${GEMINI_API_KEY}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contents: [{
              parts: [{
                text: `あなたは医療カルテの入力アシスタントです。以下の会話テキストから患者情報を抽出してください。

会話テキスト:
${text}

以下のJSON形式で回答してください。該当する情報がない場合は空文字列にしてください:
{
  "lastName": "姓（日本語のまま）",
  "firstName": "名（日本語のまま）",
  "gender": "male または female または other",
  "birthDate": "YYYY-MM-DD形式の生年月日",
  "diseaseName": "病名（複数ある場合は読点「、」で区切って列挙）",
  "diseaseDetail": "病名の詳細情報",
  "treatment": "治療内容"
}

注意事項:
- 名前は日本語のままで抽出してください（ローマ字変換や子音変換はしないでください）
- 例：山田太郎 → lastName: "山田", firstName: "太郎"
- 例：鈴木花子 → lastName: "鈴木", firstName: "花子"
- 性別は「男性」「男」→male、「女性」「女」→female
- 年齢から生年を推定して生年月日を設定してください（例：33歳→約1991年生まれ）
- 病名は以下のリストから該当するものを選んでください（複数可）:
  潰瘍性大腸炎、自閉スペクトラム症、過敏性腸症候群、アトピー・アレルギー、便秘症、うつ病、クローン病、認知症、パーキンソン病、肥満症、糖尿病、慢性疲労症候群
- リストにない病名も検出された場合はそのまま記載してください
- 複数の病名が話された場合は、すべて「、」区切りで記載してください（例：「潰瘍性大腸炎、うつ病」）

JSON形式のみで回答し、他の説明は不要です。`
              }]
            }],
            generationConfig: {
              temperature: 0.1,
              maxOutputTokens: 1024,
            }
          }),
        }
      )

      if (!response.ok) {
        throw new Error('Gemini API error')
      }

      const data = await response.json()
      const content = data.candidates?.[0]?.content?.parts?.[0]?.text

      if (content) {
        const jsonMatch = content.match(/\{[\s\S]*\}/)
        if (jsonMatch) {
          const patientData = JSON.parse(jsonMatch[0])
          const filteredData: PatientFormData = {}
          
          if (patientData.lastName) filteredData.lastName = patientData.lastName
          if (patientData.firstName) filteredData.firstName = patientData.firstName
          if (patientData.gender) filteredData.gender = patientData.gender
          if (patientData.birthDate) filteredData.birthDate = patientData.birthDate
          if (patientData.diseaseName) filteredData.diseaseName = patientData.diseaseName
          if (patientData.diseaseDetail) filteredData.diseaseDetail = patientData.diseaseDetail
          if (patientData.treatment) filteredData.treatment = patientData.treatment
          
          setExtractedData(filteredData)
          onFormUpdate(filteredData)
        }
      }
    } catch (err) {
      console.error('Patient info extraction error:', err)
    } finally {
      setIsProcessing(false)
    }
  }, [onFormUpdate])

  const startRecording = useCallback(async () => {
    try {
      setError(null)
      setLiveTranscript('')
      setExtractedData({})
      confirmedTextRef.current = ''
      lastProcessedRef.current = ''
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { channelCount: 1, sampleRate: 16000 } 
      })
      streamRef.current = stream

      const socket = new WebSocket(
        `wss://api.deepgram.com/v1/listen?model=nova-3&language=ja&punctuate=true&smart_format=true&interim_results=true`,
        ['token', DEEPGRAM_API_KEY]
      )
      socketRef.current = socket

      socket.onopen = () => {
        console.log('Deepgram WebSocket connected')
        
        const mediaRecorder = new MediaRecorder(stream, {
          mimeType: 'audio/webm;codecs=opus',
        })
        mediaRecorderRef.current = mediaRecorder

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0 && socket.readyState === WebSocket.OPEN) {
            socket.send(event.data)
          }
        }

        mediaRecorder.start(100)
        setIsRecording(true)

        // 3秒ごとに患者情報を抽出
        processingIntervalRef.current = window.setInterval(() => {
          if (confirmedTextRef.current.trim()) {
            extractPatientInfo(confirmedTextRef.current)
          }
        }, 3000)
      }

      socket.onmessage = (event) => {
        const data = JSON.parse(event.data)
        if (data.channel?.alternatives?.[0]?.transcript) {
          const newTranscript = data.channel.alternatives[0].transcript
          
          if (data.is_final && newTranscript.trim()) {
            confirmedTextRef.current += newTranscript + ' '
            setLiveTranscript(confirmedTextRef.current)
          } else if (!data.is_final) {
            setLiveTranscript(confirmedTextRef.current + newTranscript)
          }
        }
      }

      socket.onerror = () => {
        setError('音声認識の接続に失敗しました')
        stopRecording()
      }

    } catch (err) {
      setError('マイクへのアクセスが拒否されました')
    }
  }, [extractPatientInfo])

  const stopRecording = useCallback(() => {
    if (processingIntervalRef.current) {
      clearInterval(processingIntervalRef.current)
      processingIntervalRef.current = null
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
    
    if (socketRef.current) {
      socketRef.current.close()
      socketRef.current = null
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }

    setIsRecording(false)

    // 最終抽出を実行
    if (confirmedTextRef.current.trim()) {
      extractPatientInfo(confirmedTextRef.current)
    }
  }, [extractPatientInfo])

  const handleClear = useCallback(() => {
    setLiveTranscript('')
    setExtractedData({})
    confirmedTextRef.current = ''
    lastProcessedRef.current = ''
    setError(null)
  }, [])

  // 自動開始
  useEffect(() => {
    if (autoStart && autoStartEnabled && !hasAutoStarted.current) {
      hasAutoStarted.current = true
      const timer = setTimeout(() => {
        startRecording()
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [])

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (processingIntervalRef.current) {
        clearInterval(processingIntervalRef.current)
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop()
      }
      if (socketRef.current) {
        socketRef.current.close()
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
      }
    }
  }, [])

  return (
    <div className="patient-voice-input">
      {/* 自動開始設定 */}
      <div className="patient-voice-input__auto-start">
        <label className="patient-voice-input__checkbox-label">
          <input
            type="checkbox"
            checked={autoStartEnabled}
            onChange={(e) => handleAutoStartChange(e.target.checked)}
          />
          <span>新規作成時に音声入力を自動開始</span>
        </label>
      </div>

      <div className="patient-voice-input__controls">
        {!isRecording ? (
          <button
            type="button"
            onClick={startRecording}
            className="patient-voice-input__button patient-voice-input__button--start"
          >
            <span className="material-symbols-outlined">mic</span>
            音声入力を開始
          </button>
        ) : (
          <button
            type="button"
            onClick={stopRecording}
            className="patient-voice-input__button patient-voice-input__button--stop"
          >
            <span className="material-symbols-outlined">stop</span>
            録音を停止
          </button>
        )}
        
        {isRecording && (
          <span className="patient-voice-input__status">
            <span className="patient-voice-input__pulse"></span>
            録音中...
          </span>
        )}

        {isProcessing && (
          <span className="patient-voice-input__processing">
            <span className="patient-voice-input__spinner"></span>
            AI解析中...
          </span>
        )}

        {liveTranscript && (
          <button
            type="button"
            onClick={handleClear}
            className="patient-voice-input__clear"
          >
            <span className="material-symbols-outlined">delete</span>
          </button>
        )}
      </div>

      {error && (
        <div className="patient-voice-input__error">
          <span className="material-symbols-outlined">error</span>
          {error}
        </div>
      )}

      {/* リアルタイム文字起こし */}
      {(liveTranscript || isRecording) && (
        <div className="patient-voice-input__live">
          <label className="patient-voice-input__label">
            <span className="material-symbols-outlined">subtitles</span>
            リアルタイム文字起こし
          </label>
          <div className="patient-voice-input__live-text">
            {liveTranscript || '患者情報を話してください...'}
            {isRecording && <span className="patient-voice-input__cursor">|</span>}
          </div>
        </div>
      )}

      {/* 抽出された患者情報 */}
      {Object.keys(extractedData).length > 0 && (
        <div className="patient-voice-input__extracted">
          <label className="patient-voice-input__label">
            <span className="material-symbols-outlined">auto_awesome</span>
            AI抽出結果（自動入力済み）
          </label>
          <div className="extracted-grid">
            {extractedData.lastName && (
              <div className="extracted-item">
                <span className="extracted-label">姓</span>
                <span className="extracted-value">{extractedData.lastName}</span>
              </div>
            )}
            {extractedData.firstName && (
              <div className="extracted-item">
                <span className="extracted-label">名</span>
                <span className="extracted-value">{extractedData.firstName}</span>
              </div>
            )}
            {extractedData.gender && (
              <div className="extracted-item">
                <span className="extracted-label">性別</span>
                <span className="extracted-value">
                  {extractedData.gender === 'male' ? '男性' : extractedData.gender === 'female' ? '女性' : 'その他'}
                </span>
              </div>
            )}
            {extractedData.birthDate && (
              <div className="extracted-item">
                <span className="extracted-label">生年月日</span>
                <span className="extracted-value">{extractedData.birthDate}</span>
              </div>
            )}
            {extractedData.diseaseName && (
              <div className="extracted-item extracted-item--full">
                <span className="extracted-label">病名</span>
                <span className="extracted-value">{extractedData.diseaseName}</span>
              </div>
            )}
            {extractedData.diseaseDetail && (
              <div className="extracted-item extracted-item--full">
                <span className="extracted-label">病名詳細</span>
                <span className="extracted-value">{extractedData.diseaseDetail}</span>
              </div>
            )}
            {extractedData.treatment && (
              <div className="extracted-item extracted-item--full">
                <span className="extracted-label">治療内容</span>
                <span className="extracted-value">{extractedData.treatment}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {!liveTranscript && !isRecording && (
        <div className="patient-voice-input__placeholder">
          <span className="material-symbols-outlined">record_voice_over</span>
          <p>患者情報を声で入力できます。例：「山田太郎さん、男性、33歳、潰瘍性大腸炎の患者です」</p>
        </div>
      )}

      <style>{`
        .patient-voice-input {
          background-color: var(--color-solid-gray25);
          border: 2px dashed var(--border-color);
          border-radius: var(--border-radius-lg);
          padding: var(--spacing-lg);
          margin-bottom: var(--spacing-lg);
        }

        .patient-voice-input__auto-start {
          margin-bottom: var(--spacing-md);
          padding-bottom: var(--spacing-md);
          border-bottom: 1px solid var(--border-color);
        }

        .patient-voice-input__checkbox-label {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          cursor: pointer;
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .patient-voice-input__checkbox-label input {
          width: 18px;
          height: 18px;
          accent-color: var(--color-blue500);
        }

        .patient-voice-input__controls {
          display: flex;
          align-items: center;
          gap: var(--spacing-md);
          margin-bottom: var(--spacing-md);
          flex-wrap: wrap;
        }

        .patient-voice-input__button {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          padding: var(--spacing-sm) var(--spacing-lg);
          border: none;
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .patient-voice-input__button--start {
          background-color: var(--color-blue500);
          color: white;
        }

        .patient-voice-input__button--start:hover {
          background-color: var(--color-blue600);
        }

        .patient-voice-input__button--stop {
          background-color: var(--color-alert);
          color: white;
          animation: pulse 1.5s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }

        .patient-voice-input__status {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          font-size: 0.875rem;
          color: var(--color-alert);
        }

        .patient-voice-input__pulse {
          width: 8px;
          height: 8px;
          background-color: var(--color-alert);
          border-radius: 50%;
          animation: blink 1s infinite;
        }

        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }

        .patient-voice-input__processing {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          font-size: 0.75rem;
          color: var(--color-blue600);
        }

        .patient-voice-input__spinner {
          width: 14px;
          height: 14px;
          border: 2px solid var(--color-blue200);
          border-top-color: var(--color-blue600);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .patient-voice-input__clear {
          padding: var(--spacing-xs);
          background: none;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
        }

        .patient-voice-input__clear:hover {
          color: var(--color-alert);
        }

        .patient-voice-input__error {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          padding: var(--spacing-sm) var(--spacing-md);
          background-color: var(--color-alert-light);
          color: var(--color-alert);
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
          margin-bottom: var(--spacing-md);
        }

        .patient-voice-input__live {
          background-color: white;
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-md);
          padding: var(--spacing-md);
          margin-bottom: var(--spacing-md);
        }

        .patient-voice-input__label {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          font-size: 0.75rem;
          font-weight: 500;
          color: var(--text-secondary);
          margin-bottom: var(--spacing-sm);
        }

        .patient-voice-input__label .material-symbols-outlined {
          font-size: 16px;
        }

        .patient-voice-input__live-text {
          font-size: 0.875rem;
          line-height: 1.6;
          color: var(--text-primary);
          min-height: 40px;
          max-height: 100px;
          overflow-y: auto;
        }

        .patient-voice-input__cursor {
          animation: cursorBlink 1s infinite;
          color: var(--color-blue500);
        }

        @keyframes cursorBlink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }

        .patient-voice-input__extracted {
          background-color: white;
          border: 1px solid var(--color-blue200);
          border-radius: var(--border-radius-md);
          padding: var(--spacing-md);
          margin-bottom: var(--spacing-md);
        }

        .extracted-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: var(--spacing-sm);
        }

        .extracted-item {
          display: flex;
          flex-direction: column;
          gap: 2px;
          padding: var(--spacing-xs);
          background-color: var(--color-blue50);
          border-radius: var(--border-radius-sm);
        }

        .extracted-item--full {
          grid-column: 1 / -1;
        }

        .extracted-label {
          font-size: 0.6875rem;
          color: var(--text-secondary);
        }

        .extracted-value {
          font-size: 0.8125rem;
          font-weight: 500;
          color: var(--color-blue700);
        }

        .patient-voice-input__placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: var(--spacing-lg);
          color: var(--text-secondary);
          text-align: center;
        }

        .patient-voice-input__placeholder .material-symbols-outlined {
          font-size: 48px;
          margin-bottom: var(--spacing-sm);
          opacity: 0.5;
        }

        .patient-voice-input__placeholder p {
          font-size: 0.875rem;
          max-width: 400px;
        }

        @media (max-width: 768px) {
          .extracted-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  )
}

